'use client'

import { RefObject, useState } from 'react'
import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react'
import { useDispatch, useSelector } from 'react-redux'
import { IRedux } from '@/_store/types'
import { useRouter } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { removePoint } from '@/_store/common/common.slice'
import YandexMap from '../components/YandexMap'
import ConfirmModal from '../../../ConfirmModal'
import AddressDetails from '../components/AddressDetails'
import TrashRed from '@/_assets/icon-components/TrashRed'
import { sessionActions } from '@/_store/session/session.slice'
import AddressSearch from '../components/YandexMap/components/AddressSearch'
import type { DeliveryZoneValue } from '@/_hooks/useDeliveryZone'
import BranchSelectSheet from '../../../BranchSelectSheet'

const UniversalHeader = dynamic(() => import('../../../Header'))

const Mobile = ({
  onClose,
  city,
  mapRef,
  onChange,
  geometry,
  errorZone,
  isLoading,
  addressName,
  deliveryZone,
  setDeliveryZone,
  markerCoords,
  accommodation,
  handleClear,
  onMapBounded,
  onAddressSelect,
}: {
  onClose: () => void
  mapRef: RefObject<null>
  geometry: [number, number]
  city: string
  errorZone: boolean
  isLoading: boolean
  addressName: string
  handleClear: () => void
  deliveryZone: DeliveryZoneValue
  setDeliveryZone: (zone: DeliveryZoneValue) => void
  markerCoords: number[]
  accommodation: string
  onAddressSelect: (e: any) => void
  onMapBounded: (e: number[]) => void
  onChange: (
    value: { address: string; city: string; house: string; location: number[] },
    isSelect: boolean
  ) => void
}) => {
  const [isDialog, setDialog] = useState(false)
  const [details, setDetails] = useState<boolean>(false)
  const [isBottomSheet, setIsBottomSheet] = useState(false)

  const router = useRouter()
  const t = useTranslations()
  const dispatch = useDispatch()

  const { editingPoint } = useSelector((state: IRedux) => state.common)
  const { data: settingsData } = useSelector((state: IRedux) => state.settings)

  const isChooseDeliveryBranch = settingsData?.choose_delivery_branch

  const handleAddress = () => {
    if (isChooseDeliveryBranch) {
      setIsBottomSheet(true)
    } else if (addressName !== '' && city !== null && !errorZone) {
      setDetails(true)
    }
  }

  const onClear = () => {
    setDetails(false)
    handleClear()
  }

  return (
    <>
      <Flex
        flex='auto'
        position='relative'
        flexDirection='column'
        maxH='100dvh'
        overflow='hidden' /* Prevent overflow issues */
      >
        {/* Top Header - Fixed Height */}
        <Flex
          flexDir='column'
          bg='background.card'
          color={'text'}
          flexShrink={0}
          position='relative'
          zIndex={1}
        >
          <UniversalHeader
            onBack={() => {
              if (editingPoint || !details) {
                // setAddressName('')
                onClose()
              }
              setDetails(false)
            }}
            title={details ? t('address_information') : t('new_address')}
            icon={details && editingPoint?.id ? <TrashRed /> : null}
            onClick={() => details && setDialog(true)}
          />
          {!details && (
            <Box
              p={4}
              w='100%'
              top='100%'
              left='0px'
              zIndex={1}
              position='absolute'
              bg='background.card'
              borderBottom='1px solid #0000000D'
            >
              <AddressSearch
                region={geometry}
                value={addressName}
                onSelect={onAddressSelect}
              />
            </Box>
          )}
        </Flex>

        {/* Map - Flexible Centered Content */}
        <Flex flex='1' overflow='hidden' pt={4}>
          <YandexMap
            mapRef={mapRef}
            geometry={geometry}
            errorZone={errorZone}
            outerLoading={isLoading}
            onLocationChange={onChange}
            deliveryZone={deliveryZone}
            onMapBounded={onMapBounded}
          />
        </Flex>

        {/* Bottom Content - Dynamic Height */}
        <Stack
          p={4}
          gap={4}
          bg='background.card'
          zIndex={1}
          flexShrink={0}
          position='relative'
          borderTop='1px solid #0000000D'
        >
          {details ? (
            <AddressDetails
              city={city}
              isDesktop={false}
              onClear={onClear}
              coords={markerCoords}
              address={addressName}
              deliveryZone={deliveryZone}
              setDeliveryZone={setDeliveryZone}
              accommodation={accommodation}
            />
          ) : (
            addressName && (
              <Box
                pt={4}
                px={4}
                position='absolute'
                bottom='100%'
                width='100%'
                left={0}
                bg='background.card'
                color={'text'}
              >
                <Text textAlign='center' fontWeight='bold'>
                  {addressName}
                </Text>
              </Box>
            )
          )}
          <Button
            onClick={handleAddress}
            size='xl'
            rounded={16}
            h='56px'
            bg='primary'
            color='primary.text'
            _hover={{
              bg: 'primary.hover',
            }}
            disabled={errorZone || isLoading || !addressName}
          >
            {isLoading ? t('location.determining_location') : t('continue')}
          </Button>
        </Stack>
      </Flex>
      <BranchSelectSheet
        isOpen={isBottomSheet}
        onClose={() => setIsBottomSheet(false)}
        afterConfirm={() => setDetails(true)}
      />

      <ConfirmModal
        isWarning={true}
        isOpen={isDialog}
        onConfirm={() => {
          if (editingPoint?.isActive) {
            dispatch(sessionActions.resetPGL())
            setTimeout(() => {
              router.refresh()
            }, 1000)
          }
          dispatch(removePoint(editingPoint?.id))

          onClose()
          onClear()
        }}
        title={t('remove_address')}
        onClose={() => setDialog(false)}
        description={''}
      />
    </>
  )
}

export default Mobile
